import { toast } from "react-toastify";
// EditHistoryModal - 編集履歴 Modal（spec.md §5.8.4）
// 右側 EditHistoryButton から開かれ、editHistory を新しい順に表示する。
// ① 変更履歴の表示：action_type を子ども向け文言＋アイコンに変換して 1 行ずつ表示。
// ② 指定履歴への復帰：各行の「もどす」→ 確認ダイアログ → その地点まで巻き戻す。
// 高さはコンテンツに関係なく固定（リスト領域 h-[400px]。空状態も同じ高さを占有）。
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound, btnDelay } from "common/utils";
import { actions } from "features/builder/slice";
import { getEditHistory } from "features/builder/api";
import { HELP_HISTORY } from "features/builder/helpPages";
import { recordEditHistory } from "features/builder/hook";
import { useBuilderContext } from "features/builder/context";
import { PieceMilestone, PieceEditHistoryEntry } from "features/builder/types";
import {
  InScreenModal,
  BuilderModalPanel,
  BuilderModalAccent,
  BuilderConfirmDialog,
} from "features/builder/elements";
import { BuilderHelpPopup } from "./BuilderHelpPopup";
import { resolveHistoryLabel, isMilestoneAction, resolveMilestone } from "./editHistoryLabels";

export const EditHistoryModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const dispatch = useDispatch<AppDispatch>();
  const editHistory = useSelector((state: RootState) => state.builder.editHistory);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const { triggerReload } = useBuilderContext();
  // 「もどす」確認中の対象履歴（null = 確認ダイアログ非表示）
  const [restoreTarget, setRestoreTarget] = useState<PieceEditHistoryEntry | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  // 見られる区切りの数（-1 は全部）と、区切りの総数。超えたら注記を出す
  const [visible, setVisible] = useState<{ limit: number; total: number } | null>(null);

  // 開いたときにサーバ権威の履歴へ同期（seq を確定し、復元の整合性を担保）
  useEffect(() => {
    if (!open || !pieceId || pieceId === "local-map") return;
    getEditHistory({ piece_id: pieceId })
      .then((history) => {
        dispatch(actions.setEditHistory(history.entries));
        setVisible({ limit: history.limit, total: history.total_milestones });
      })
      .catch(() => undefined);
  }, [open, pieceId, dispatch]);

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  // 新しい順（最新が先頭）で表示
  const entries = [...editHistory].reverse();
  // 「いま」は一番新しい行に付ける。undo / redo・restore も記録に残るので、一番新しい行がいまの画面と同じ
  const latestSeq = entries[0]?.seq ?? null;
  const limited = !!visible && visible.limit >= 0 && visible.total > visible.limit;

  const handleRestoreClick = (entry: PieceEditHistoryEntry) => {
    play();
    btnDelay(() => setRestoreTarget(entry));
  };

  const handleRestoreConfirm = () => {
    if (!restoreTarget?.map) {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
      setRestoreTarget(null);
      return;
    }
    const seq = restoreTarget.seq;
    // エントリの snapshot をエディタへ適用する。後ろの記録は消さず、restore も区切りとして記録に残す。
    // restore は undo で取り消せる（docs/edit-history-undo.md）
    dispatch(actions.restoreToHistoryEntry({ seq }));
    dispatch(recordEditHistory(PieceMilestone.RESTORE, { seq }));
    triggerReload(); // map 反映後に 3D シーンを再構築
    setRestoreTarget(null);
    btnDelay(() => onClose());
  };

  return (
    <>
      <InScreenModal open={open} onBackdropClose={handleClose}>
        <BuilderModalPanel
          accent={BuilderModalAccent.BLUE}
          icon="history"
          title={`${I18n.t("MSG_BUILDER_EDIT_HISTORY")}（${entries.length}）`}
          width={720}
          onClose={handleClose}
          onHelp={() => setShowHelp(true)}
        >
          {/* 高さ固定：件数・空に関わらず常に h-[440px] */}
          <div className="flex-col-el items-stretch w-full h-[440px]">
            {entries.length === 0 ? (
              <div className="flex-col-el flex-center w-full h-full">
                <span className="type-text type-body text-textcolor-black/40">
                  {I18n.t("MSG_BUILDER_EDIT_HISTORY_EMPTY")}
                </span>
              </div>
            ) : (
              <div className="flex-col-el items-stretch w-full h-full gap-2 overflow-y-auto invisible-scrollbar">
                {entries.map((entry) => {
                  // マイルストーン（完成・公開・非公開）はチャットの切り線として描画
                  const isLatest = entry.seq === latestSeq;
                  // 区切り（開いた・閉じた・完成・公開など）はチャットの切り線として描画。そのときの中身があれば restore できる
                  if (isMilestoneAction(entry.action_type)) {
                    const { label, icon, pillClass } = resolveMilestone(entry);
                    return (
                      <div key={entry.seq} className="flex-row-el items-center w-full gap-2 py-1">
                        <div className="flex-1 h-[2px] bg-gray2-30" />
                        <div
                          className={`flex-row-center shrink-0 gap-1 px-3 py-1 rounded-full ${pillClass}`}
                        >
                          <span className="material-symbols-outlined text-white type-sm">
                            {icon}
                          </span>
                          <span className="type-text type-sm text-white whitespace-nowrap">
                            {label}
                          </span>
                        </div>
                        <span className="type-text-left type-sm text-textcolor-black/50 whitespace-nowrap">
                          {dayjs(entry.created_at).format("HH:mm")}
                        </span>
                        <div className="flex-1 h-[2px] bg-gray2-30" />
                        {isLatest ? (
                          <span className="flex-col-center shrink-0 rounded-full bg-green-100 px-3 py-1 type-sm text-white whitespace-nowrap">
                            {I18n.t("MSG_BUILDER_EDIT_HISTORY_NOW")}
                          </span>
                        ) : (
                          entry.map && (
                            <div className="upsilon shrink-0">
                              <button
                                type="button"
                                onClick={() => handleRestoreClick(entry)}
                                className="type-inherit-font btn btn-blue whitespace-nowrap"
                              >
                                <div className="flex-row-center gap-1">
                                  <span className="material-symbols-outlined text-white type-sm">
                                    undo
                                  </span>
                                  <span className="type-text type-sm text-white">
                                    {I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE")}
                                  </span>
                                </div>
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    );
                  }

                  const { label, icon } = resolveHistoryLabel(entry);
                  return (
                    <div
                      key={entry.seq}
                      className="flex-row-el items-center w-full gap-3 bg-beige-60 rounded-xl p-3"
                    >
                      {/* アクションアイコン（丸座） */}
                      <div className="flex-col-center shrink-0 w-10 h-10 rounded-xl bg-blue-100 shadow-md">
                        <span className="material-symbols-outlined text-white type-md">{icon}</span>
                      </div>

                      {/* ラベル＋（V番号・時刻） */}
                      <div className="flex-col-el items-start flex-1 min-w-0 gap-1">
                        <span className="type-text-left type-body text-textcolor-black w-full truncate">
                          {label}
                        </span>
                        <div className="flex-row-el items-center gap-2">
                          <span className="flex-col-center shrink-0 rounded-full bg-blue-10 px-2 type-sm text-blue-100 whitespace-nowrap">
                            {`V${entry.version}`}
                          </span>
                          <span className="type-text-left type-sm text-textcolor-black/50 whitespace-nowrap">
                            {dayjs(entry.created_at).format("HH:mm")}
                          </span>
                        </div>
                      </div>

                      {/* 最新行は「いま」バッジ、それ以外は「もどす」ボタン */}
                      {isLatest ? (
                        <span className="flex-col-center shrink-0 rounded-full bg-green-100 px-3 py-1 type-sm text-white whitespace-nowrap">
                          {I18n.t("MSG_BUILDER_EDIT_HISTORY_NOW")}
                        </span>
                      ) : (
                        <div className="upsilon shrink-0">
                          <button
                            type="button"
                            onClick={() => handleRestoreClick(entry)}
                            className="type-inherit-font btn btn-blue whitespace-nowrap"
                          >
                            <div className="flex-row-center gap-1">
                              <span className="material-symbols-outlined text-white type-sm">
                                undo
                              </span>
                              <span className="type-text type-sm text-white">
                                {I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE")}
                              </span>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* 見られる件数の注記。高さは出ないときも同じ（周りを動かさない） */}
          <div className="flex-col-el flex-center w-full h-8 px-4">
            {limited && (
              <span className="type-text type-sm text-textcolor-black/50">
                {I18n.t("MSG_BUILDER_EDIT_HISTORY_LIMIT_NOTE", {
                  limit: visible.limit,
                  total: visible.total,
                })}
              </span>
            )}
          </div>
        </BuilderModalPanel>
      </InScreenModal>

      {/* 復帰確認（誤タップ防止） */}
      <BuilderConfirmDialog
        visible={!!restoreTarget}
        accent={BuilderModalAccent.ORANGE}
        icon="history"
        confirmIcon="undo"
        confirmLabel={I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE")}
        confirmSound={`${Constants.assetHost}/assets/sounds/switch-on.mp3`}
        title={I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE_CONFIRM_TITLE")}
        message={I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE_CONFIRM_MSG")}
        close={() => setRestoreTarget(null)}
        handleConfirm={handleRestoreConfirm}
      />

      <BuilderHelpPopup
        open={showHelp}
        onClose={() => setShowHelp(false)}
        chapters={HELP_HISTORY}
      />
    </>
  );
};
